// =====================================================================
// myCRM — Contacts (list + detail) and MEDDPICCR iframe wrapper
// Split from the former contacts-leads.jsx so each page lives in a
// single file. Lead-side dialogs and pages now live in pages/leads.jsx.
// =====================================================================

// Persona ICPs the MEDDPICCR checklist supports. The `slug` value is what
// gets stored on contacts.icp and used as a path segment when iframing
// the corresponding /intcrm/meddpicc/<slug>.html page.
const ICP_OPTIONS = [
  { slug: "compliance-executive", label: "Compliance Executive",   short: "CE",  icon: "⚖" },
  { slug: "engineering-leader",   label: "Engineering Leader",     short: "EL",  icon: "⚙" },
  { slug: "program-manager",      label: "Program Manager",        short: "PM",  icon: "◧" },
  { slug: "transformation-cto",   label: "Transformation CTO",     short: "CTO", icon: "◆" },
  { slug: "champion",             label: "Champion (Dev Advocate)", short: "CH",  icon: "★" },
  { slug: "influencer",           label: "Influencer (Catalyst)",   short: "IN",  icon: "◉" },
];
const ICP_BY_SLUG = Object.fromEntries(ICP_OPTIONS.map(o => [o.slug, o]));

// Role in a deal lives on the OPPORTUNITY_CONTACTS junction now, not on
// the contact itself — same person can be Champion on one deal and
// Economic Buyer on another. Helpers: getOppContactRole / getActiveOppForContact.

function MeddpiccIcon({ icp, contactId, navigate, size = "sm" }) {
  if (!icp || !ICP_BY_SLUG[icp]) return null;
  // The MEDDPICCR checklist is deal-scoped, so the chip is only clickable
  // if the contact has at least one open opportunity to anchor the
  // questionnaire. No open opp → render the icon as a muted, disabled chip
  // with a tooltip explaining the gap.
  const activeOpp = getActiveOppForContact(contactId);
  const o = ICP_BY_SLUG[icp];
  const isLg = size === "lg";
  const cls = isLg ? "btn btn-primary btn-sm" : "meddpicc-chip";
  const baseStyle = isLg
    ? { display: "inline-flex", alignItems: "center", gap: 6 }
    : { padding: "2px 8px", border: "1px solid var(--rule, #d1d5db)", borderRadius: 6, fontSize: 12.5, display: "inline-flex", alignItems: "center", gap: 4, background: "#fff", color: "#1c1917" };
  if (!activeOpp) {
    return (
      <span
        className={cls}
        title="Add this contact to an opportunity to open the MEDDPICCR checklist."
        style={Object.assign({}, baseStyle, { opacity: 0.5, cursor: "not-allowed" })}
      >
        <span aria-hidden="true">{o.icon}</span>
        <span>{isLg ? `MEDDPICCR · ${o.short}` : o.short}</span>
      </span>
    );
  }
  return (
    <a
      className={cls}
      title={`Open MEDDPICCR · ${o.label} · ${activeOpp.name}`}
      onClick={(e) => { e.stopPropagation(); navigate("sales/contacts/" + contactId + "/meddpicc"); }}
      style={Object.assign({}, baseStyle, { cursor: "pointer" })}
    >
      <span aria-hidden="true">{o.icon}</span>
      <span>{isLg ? `MEDDPICCR · ${o.short}` : o.short}</span>
    </a>
  );
}

// `defaultAccountId` pre-selects and locks the account (used from an
// account's Contacts tab). `navigate` is optional — when omitted (e.g. the
// account-scoped add) the dialog just closes and the list refreshes in
// place rather than jumping to the new contact's detail page.
function NewContactDialog({ onClose, navigate, defaultAccountId }) {
  // Role in deal is no longer captured at contact-create time — it lives
  // on the opportunity_contacts junction, set when the contact is attached
  // to a specific deal (Stakeholders panel on the opportunity).
  const [form, setForm] = useState({
    first: "", last: "", title: "", email: "", phone: "",
    accountId: defaultAccountId || "", dept: "", seniority: "",
    channel: "", gdpr: "Unknown", icp: "", source: "",
  });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  function set(key, val) { setForm(f => ({ ...f, [key]: val })); }

  async function handleSave() {
    if (!form.first.trim() || !form.last.trim()) {
      setError("First and last name are required.");
      return;
    }
    const email = (form.email || "").trim();
    if (!email || !/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(email)) {
      setError("A valid email is required.");
      return;
    }
    setSaving(true);
    setError(null);
    try {
      const id = "c_" + Math.random().toString(36).slice(2, 10);
      const created = await tpAPI.contacts.create({ id, ...form, email });
      onClose();
      if (navigate) navigate("sales/contacts/" + (created.id || id));
    } catch (e) {
      setError(e.message || "Failed to save contact.");
      setSaving(false);
    }
  }

  const accounts = D.ACCOUNTS || [];

  return (
    <Dialog
      title="New contact"
      onClose={onClose}
      footer={
        <>
          <button className="btn btn-sm" onClick={onClose}>Cancel</button>
          <button className="btn btn-primary btn-sm" onClick={handleSave} disabled={saving}>
            {saving ? "Saving…" : "Save contact"}
          </button>
        </>
      }
    >
      <div className="form-row">
        <div className="form-field">
          <label>First name *</label>
          <input value={form.first} onChange={e => set("first", e.target.value)} placeholder="Jane" autoFocus />
        </div>
        <div className="form-field">
          <label>Last name *</label>
          <input value={form.last} onChange={e => set("last", e.target.value)} placeholder="Smith" />
        </div>
      </div>
      <div className="form-field">
        <label>Job title</label>
        <input value={form.title} onChange={e => set("title", e.target.value)} placeholder="VP Engineering" />
      </div>
      <div className="form-row">
        <div className="form-field">
          <label>Email *</label>
          <input type="email" required value={form.email} onChange={e => set("email", e.target.value)} placeholder="jane@company.com" />
        </div>
        <div className="form-field">
          <label>Phone</label>
          <input value={form.phone} onChange={e => set("phone", e.target.value)} placeholder="+1 555 000 0000" />
        </div>
      </div>
      <div className="form-field">
        <label>Account</label>
        <select value={form.accountId} onChange={e => set("accountId", e.target.value)} disabled={!!defaultAccountId}>
          <option value="">— None —</option>
          {accounts.map(a => <option key={a.id} value={a.id}>{a.name}</option>)}
        </select>
      </div>
      <div className="form-row">
        <div className="form-field">
          <label>Seniority</label>
          <select value={form.seniority} onChange={e => set("seniority", e.target.value)}>
            <option value="">— None —</option>
            <option>C-Suite</option><option>VP</option><option>Director</option>
            <option>Manager</option><option>Individual Contributor</option>
          </select>
        </div>
        <div className="form-field">
          <label>Department</label>
          <select value={form.dept} onChange={e => set("dept", e.target.value)}>
            <option value="">— None —</option>
            {D.DEPARTMENTS.map(d => <option key={d} value={d}>{d}</option>)}
            {form.dept && !D.DEPARTMENTS.includes(form.dept) && (
              <option value={form.dept}>{form.dept} (legacy)</option>
            )}
          </select>
        </div>
      </div>
      <div className="form-row">
        <div className="form-field">
          <label>Channel preference</label>
          <select value={form.channel} onChange={e => set("channel", e.target.value)}>
            <option value="">— None —</option>
            <option>Email</option><option>Phone</option><option>LinkedIn</option>
          </select>
        </div>
        <div className="form-field">
          <label>GDPR consent</label>
          <select value={form.gdpr} onChange={e => set("gdpr", e.target.value)}>
            <option>Unknown</option><option>Opted in</option><option>Opted out</option>
          </select>
        </div>
      </div>
      <div className="form-row">
        <div className="form-field">
          <label>ICP persona</label>
          <select value={form.icp} onChange={e => set("icp", e.target.value)}>
            <option value="">— None —</option>
            {ICP_OPTIONS.map(o => <option key={o.slug} value={o.slug}>{o.label}</option>)}
          </select>
        </div>
        <div className="form-field">
          <label>Source</label>
          <select value={form.source} onChange={e => set("source", e.target.value)}>
            <option value="">— Unknown —</option>
            <option>LinkedIn</option><option>Ads</option><option>Published content</option>
            <option>Referral</option><option>Event</option><option>Network</option><option>Outbound</option><option>Other</option>
          </select>
        </div>
      </div>
      <p className="muted" style={{ margin: "4px 0 0", fontSize: 12 }}>
        Role in deal (Champion, Economic Buyer, etc.) is set per opportunity in the deal's Stakeholders panel.
      </p>
      {error && <div className="form-error">{error}</div>}
    </Dialog>
  );
}

// Bulk-import contacts into one account from pasted CSV or a chosen .csv
// file. Account-scoped: every row is forced onto `accountId`, so an
// `account` column in the file is ignored. Rows post sequentially through
// the low-level request() (skipping the per-row reload), then the contacts
// slice is refreshed once at the end so the account's list updates in place.
function BulkUploadContactsDialog({ accountId, accountName, onClose }) {
  const [text, setText]           = useState("");
  const [parsed, setParsed]       = useState(null);   // { rows, errors }
  const [importing, setImporting] = useState(false);
  const [progress, setProgress]   = useState(0);
  const [result, setResult]       = useState(null);   // { created, failed:[{email,reason}] }
  const [error, setError]         = useState(null);

  // Header synonyms -> contact field. Unknown columns are ignored. first,
  // last and email are required; the rest are optional.
  const FIELD_MAP = {
    "first": "first", "firstname": "first", "first name": "first", "given name": "first",
    "last": "last", "lastname": "last", "last name": "last", "surname": "last", "family name": "last",
    "email": "email", "email address": "email", "e-mail": "email", "mail": "email",
    "title": "title", "job title": "title", "jobtitle": "title", "position": "title",
    "phone": "phone", "mobile": "phone", "phone number": "phone", "tel": "phone",
    "dept": "dept", "department": "dept",
    "seniority": "seniority", "level": "seniority",
    "channel": "channel",
    "icp": "icp", "persona": "icp",
    "source": "source",
  };
  const EMAIL_RE = /^[^@\s]+@[^@\s]+\.[^@\s]+$/;

  // Minimal RFC-4180-ish parser: handles quoted fields, "" escapes and
  // CR / LF / CRLF line endings. Returns an array of cell arrays.
  function parseCsv(raw) {
    const grid = [];
    let field = "", row = [], inQuotes = false;
    function endRow() {
      row.push(field); field = "";
      if (row.length > 1 || row[0] !== "") grid.push(row);
      row = [];
    }
    for (let i = 0; i < raw.length; i++) {
      const ch = raw[i];
      if (inQuotes) {
        if (ch === '"') {
          if (raw[i + 1] === '"') { field += '"'; i++; } else inQuotes = false;
        } else field += ch;
      } else if (ch === '"') {
        inQuotes = true;
      } else if (ch === ",") {
        row.push(field); field = "";
      } else if (ch === "\n" || ch === "\r") {
        if (ch === "\r" && raw[i + 1] === "\n") i++;
        endRow();
      } else field += ch;
    }
    if (field !== "" || row.length) endRow();
    return grid;
  }

  function onFile(e) {
    const f = e.target.files && e.target.files[0];
    if (!f) return;
    const reader = new FileReader();
    reader.onload = () => { setText(String(reader.result || "")); setParsed(null); setResult(null); setError(null); };
    reader.onerror = () => setError("Could not read that file.");
    reader.readAsText(f);
  }

  function doParse() {
    setError(null); setResult(null);
    const grid = parseCsv(text.trim());
    if (grid.length < 2) { setParsed(null); setError("Need a header row plus at least one contact row."); return; }
    const fields = grid[0].map(h => FIELD_MAP[(h || "").trim().toLowerCase()] || null);
    if (!fields.includes("first") || !fields.includes("last") || !fields.includes("email")) {
      setParsed(null); setError("CSV must have columns for first, last and email (header row).");
      return;
    }
    const rows = [], errors = [];
    for (let r = 1; r < grid.length; r++) {
      const cells = grid[r];
      const rec = {};
      fields.forEach((f, idx) => { if (f) rec[f] = (cells[idx] || "").trim(); });
      if (!rec.first || !rec.last) { errors.push({ rowNum: r + 1, reason: "missing first/last name" }); continue; }
      if (!rec.email || !EMAIL_RE.test(rec.email)) { errors.push({ rowNum: r + 1, reason: "missing or invalid email" }); continue; }
      rows.push(rec);
    }
    setParsed({ rows, errors });
  }

  async function doImport() {
    if (!parsed || !parsed.rows.length) return;
    setImporting(true); setError(null); setProgress(0);
    const failed = [];
    let created = 0;
    for (const rec of parsed.rows) {
      try {
        await tpAPI.request("/apicrm/contacts", {
          method: "POST",
          body: { id: "c_" + Math.random().toString(36).slice(2, 10), accountId, gdpr: "Unknown", ...rec },
        });
        created++;
      } catch (e) {
        failed.push({ email: rec.email, reason: e.message || "failed" });
      }
      setProgress(created + failed.length);
    }
    // One refresh after the whole batch rather than per row.
    if (typeof window.tpReload === "function") { try { await window.tpReload("contacts"); } catch (e) { /* non-fatal */ } }
    setImporting(false);
    setResult({ created, failed });
  }

  const total = parsed ? parsed.rows.length : 0;

  return (
    <Dialog
      title={"Upload contacts · " + accountName}
      onClose={onClose}
      footer={result ? (
        <button className="btn btn-primary btn-sm" onClick={onClose}>Done</button>
      ) : (<>
        <button className="btn btn-sm" onClick={onClose}>Cancel</button>
        {parsed
          ? <button className="btn btn-primary btn-sm" onClick={doImport} disabled={importing || total === 0}>
              {importing ? `Importing… (${progress}/${total})` : `Import ${total} contact${total === 1 ? "" : "s"}`}
            </button>
          : <button className="btn btn-primary btn-sm" onClick={doParse} disabled={!text.trim()}>Preview</button>}
      </>)}
    >
      {result ? (
        <>
          <div className="quick-saved">✓ Imported {result.created} contact{result.created === 1 ? "" : "s"} into {accountName}.</div>
          {result.failed.length > 0 && (
            <div className="form-field" style={{ marginTop: 10 }}>
              <label>{result.failed.length} row{result.failed.length === 1 ? "" : "s"} failed</label>
              <table className="tbl tbl-flush">
                <thead><tr><th>Email</th><th>Reason</th></tr></thead>
                <tbody>{result.failed.map((f, i) => (
                  <tr key={i} style={{ cursor: "default" }}><td>{f.email || "—"}</td><td className="muted">{f.reason}</td></tr>
                ))}</tbody>
              </table>
            </div>
          )}
        </>
      ) : (<>
        <p className="muted" style={{ marginTop: 0, fontSize: 12.5 }}>
          Paste CSV or choose a <code>.csv</code> file. The first row must be a header.
          Required columns: <strong>first</strong>, <strong>last</strong>, <strong>email</strong>.
          Optional: title, phone, dept, seniority, channel, icp, source. Every contact is added to <strong>{accountName}</strong>.
        </p>
        <div className="form-field">
          <label>CSV file</label>
          <input type="file" accept=".csv,text/csv,text/plain" onChange={onFile} />
        </div>
        <div className="form-field">
          <label>…or paste CSV</label>
          <textarea
            value={text}
            onChange={e => { setText(e.target.value); setParsed(null); setResult(null); setError(null); }}
            placeholder={"first,last,email,title\nJane,Smith,jane@acme.com,VP Engineering\nAmir,Khan,amir@acme.com,Director of Security"}
            style={{ minHeight: 130, fontFamily: "var(--mono, monospace)", fontSize: 12.5 }}
          />
        </div>
        {parsed && (
          <div className="muted" style={{ fontSize: 12.5 }}>
            Ready to import <strong>{parsed.rows.length}</strong> valid row{parsed.rows.length === 1 ? "" : "s"}
            {parsed.errors.length > 0 && <> · <span style={{ color: "var(--red)" }}>{parsed.errors.length} skipped</span></>}.
            {parsed.errors.length > 0 && (
              <ul style={{ margin: "6px 0 0", paddingLeft: 18 }}>
                {parsed.errors.slice(0, 8).map((er, i) => <li key={i}>Row {er.rowNum}: {er.reason}</li>)}
                {parsed.errors.length > 8 && <li>…and {parsed.errors.length - 8} more</li>}
              </ul>
            )}
          </div>
        )}
        {error && <div className="form-error">{error}</div>}
      </>)}
    </Dialog>
  );
}

function EditContactDialog({ contact, onClose }) {
  // Role in deal is per-opportunity now (see opportunity_contacts) — this
  // dialog only edits identity / consent / persona, not deal-specific role.
  const [form, setForm] = useState({
    first: contact.first || "",
    last: contact.last || "",
    title: contact.title || "",
    seniority: contact.seniority || "",
    dept: contact.dept || "",
    email: contact.email || "",
    phone: contact.phone || "",
    accountId: contact.accountId || "",
    channel: contact.channel || "",
    gdpr: contact.gdpr || "Unknown",
    optOut: contact.optOut ? "Yes" : "No",
    icp: contact.icp || "",
    source: contact.source || "",
  });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  function set(key, val) { setForm(f => ({ ...f, [key]: val })); }

  async function handleSave() {
    if (!form.first.trim() || !form.last.trim()) {
      setError("First and last name are required.");
      return;
    }
    const email = (form.email || "").trim();
    if (!email || !/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(email)) {
      setError("A valid email is required.");
      return;
    }
    setSaving(true);
    setError(null);
    try {
      await tpAPI.contacts.update(contact.id, {
        first: form.first.trim(),
        last: form.last.trim(),
        title: form.title.trim(),
        seniority: form.seniority,
        dept: form.dept.trim(),
        email,
        phone: form.phone.trim(),
        accountId: form.accountId || null,
        channel: form.channel || null,
        gdpr: form.gdpr,
        optOut: form.optOut === "Yes",
        icp: form.icp || null,
        source: form.source || null,
      });
      onClose();
    } catch (e) {
      setError(e.message || "Failed to save contact.");
      setSaving(false);
    }
  }

  const accounts = D.ACCOUNTS || [];
  return (
    <Dialog
      title="Edit contact"
      onClose={onClose}
      footer={
        <>
          <button className="btn btn-sm" onClick={onClose}>Cancel</button>
          <button className="btn btn-primary btn-sm" onClick={handleSave} disabled={saving}>
            {saving ? "Saving…" : "Save changes"}
          </button>
        </>
      }
    >
      <div className="form-row">
        <div className="form-field">
          <label>First name *</label>
          <input value={form.first} onChange={e => set("first", e.target.value)} autoFocus />
        </div>
        <div className="form-field">
          <label>Last name *</label>
          <input value={form.last} onChange={e => set("last", e.target.value)} />
        </div>
      </div>
      <div className="form-field">
        <label>Job title</label>
        <input value={form.title} onChange={e => set("title", e.target.value)} placeholder="VP Engineering" />
      </div>
      <div className="form-row">
        <div className="form-field">
          <label>Seniority</label>
          <select value={form.seniority} onChange={e => set("seniority", e.target.value)}>
            <option value="">— None —</option>
            <option>C-Suite</option><option>VP</option><option>Director</option>
            <option>Manager</option><option>Individual Contributor</option>
          </select>
        </div>
        <div className="form-field">
          <label>Department</label>
          <select value={form.dept} onChange={e => set("dept", e.target.value)}>
            <option value="">— None —</option>
            {D.DEPARTMENTS.map(d => <option key={d} value={d}>{d}</option>)}
            {form.dept && !D.DEPARTMENTS.includes(form.dept) && (
              <option value={form.dept}>{form.dept} (legacy)</option>
            )}
          </select>
        </div>
      </div>
      <div className="form-row">
        <div className="form-field">
          <label>Email *</label>
          <input type="email" required value={form.email} onChange={e => set("email", e.target.value)} placeholder="jane@company.com" />
        </div>
        <div className="form-field">
          <label>Phone</label>
          <input value={form.phone} onChange={e => set("phone", e.target.value)} placeholder="+1 555 000 0000" />
        </div>
      </div>
      <div className="form-field">
        <label>Account</label>
        <select value={form.accountId} onChange={e => set("accountId", e.target.value)}>
          <option value="">— None —</option>
          {accounts.map(a => <option key={a.id} value={a.id}>{a.name}</option>)}
        </select>
      </div>
      <div className="form-row">
        <div className="form-field">
          <label>Channel preference</label>
          <select value={form.channel} onChange={e => set("channel", e.target.value)}>
            <option value="">— None —</option>
            <option>Email</option><option>Phone</option><option>LinkedIn</option>
          </select>
        </div>
        <div className="form-field">
          <label>GDPR consent</label>
          <select value={form.gdpr} onChange={e => set("gdpr", e.target.value)}>
            <option>Unknown</option><option>Opted in</option><option>Opted out</option>
            <option>Legitimate interest</option>
          </select>
        </div>
      </div>
      <div className="form-row">
        <div className="form-field">
          <label>ICP persona</label>
          <select value={form.icp} onChange={e => set("icp", e.target.value)}>
            <option value="">— None —</option>
            {ICP_OPTIONS.map(o => <option key={o.slug} value={o.slug}>{o.label}</option>)}
          </select>
        </div>
        <div className="form-field">
          <label>Email opt-out</label>
          <select value={form.optOut} onChange={e => set("optOut", e.target.value)}>
            <option>No</option><option>Yes</option>
          </select>
        </div>
      </div>
      <div className="form-field">
        <label>Source</label>
        <select value={form.source} onChange={e => set("source", e.target.value)}>
          <option value="">— Unknown —</option>
          <option>LinkedIn</option><option>Ads</option><option>Published content</option>
          <option>Referral</option><option>Event</option><option>Network</option><option>Outbound</option><option>Other</option>
        </select>
      </div>
      <p className="muted" style={{ margin: "4px 0 0", fontSize: 12 }}>
        Role in deal is per-opportunity — edit it from the deal's Stakeholders panel.
      </p>
      {error && <div className="form-error">{error}</div>}
    </Dialog>
  );
}

function AddToSequenceDialog({ contact, onClose }) {
  const seqs = D.SEQUENCES || [];
  const [seqId, setSeqId] = useState(seqs[0]?.id || "");
  const [saving, setSaving] = useState(false);
  const [done, setDone] = useState(false);
  const [error, setError] = useState(null);

  async function handleSave() {
    if (!seqId) return;
    const already = (D.ENROLLMENTS || []).some(e => e.sequenceId === seqId && e.contactId === contact.id);
    if (already) { setError(`${contact.first} is already enrolled in this sequence.`); return; }
    setSaving(true); setError(null);
    try {
      await tpAPI.enrollments.create({
        id: "enr_" + Math.random().toString(36).slice(2, 9),
        sequenceId: seqId,
        contactId: contact.id,
        enrolledOn: new Date().toISOString().slice(0, 10),
        status: "Active",
      });
      setDone(true);
      setTimeout(onClose, 1400);
    } catch (e) {
      setError(e.message || "Failed to enrol.");
      setSaving(false);
    }
  }

  return (
    <Dialog
      title="Add to sequence"
      onClose={onClose}
      footer={!done && (
        <>
          <button className="btn btn-sm" onClick={onClose}>Cancel</button>
          <button className="btn btn-primary btn-sm" onClick={handleSave} disabled={saving || !seqId}>
            {saving ? "Enrolling…" : "Enroll"}
          </button>
        </>
      )}
    >
      {done ? (
        <div className="quick-saved">✓ {contact.first} {contact.last} enrolled</div>
      ) : <>
        <div className="form-field">
          <label>Contact</label>
          <input value={`${contact.first} ${contact.last}${contact.title ? " · " + contact.title : ""}`} disabled />
        </div>
        <div className="form-field">
          <label>Sequence</label>
          <select value={seqId} onChange={e => { setSeqId(e.target.value); setError(null); }}>
            <option value="">— Select sequence —</option>
            {seqs.map(s => <option key={s.id} value={s.id}>{s.name}{s.type ? " · " + s.type : ""}</option>)}
          </select>
        </div>
        {error && <div className="form-error">{error}</div>}
      </>}
    </Dialog>
  );
}

function ContactsList({ navigate }) {
  const [filterRole, setRole] = useState("all");
  const [filterSeg, setSeg] = useState("all");
  const [search, setSearch] = useState("");
  const [showNew, setShowNew] = useState(false);
  // Per-row derived state: role + opportunity name come from the contact's
  // most-recent open opportunity (option B). When the contact has no open
  // deal, role is null and the column shows an em dash — the MEDDPICCR chip
  // is also disabled in that case (see MeddpiccIcon).
  function activeRoleFor(c) {
    const opp = getActiveOppForContact(c.id);
    if (!opp) return { opp: null, role: null };
    const oc = getOppContactRole(opp.id, c.id);
    return { opp, role: oc ? oc.role : null };
  }
  const rows = D.CONTACTS.filter(c => {
    const a = getAccount(c.accountId);
    if (filterRole !== "all" && activeRoleFor(c).role !== filterRole) return false;
    if (filterSeg !== "all" && a.segment !== filterSeg) return false;
    if (search && !`${c.first} ${c.last} ${c.title}`.toLowerCase().includes(search.toLowerCase())) return false;
    return true;
  });
  return (
    <>
      <PageHead title="Contacts" sub={`${rows.length} contacts`} actions={
        <button className="btn btn-primary btn-sm" onClick={() => setShowNew(true)}>+ New contact</button>
      } />
      <div className="card">
        <FilterToolbar count={rows.length} total={D.CONTACTS.length}>
          <input type="text" placeholder="Search…" value={search} onChange={e => setSearch(e.target.value)} />
          <select value={filterRole} onChange={e => setRole(e.target.value)}>
            <option value="all">All roles</option><option>Champion</option><option>Economic Buyer</option><option>Influencer</option><option>End User</option><option>Blocker</option><option>Technical Evaluator</option>
          </select>
          <select value={filterSeg} onChange={e => setSeg(e.target.value)}>
            <option value="all">All segments</option><option>SME</option><option>Mid-market</option><option>Enterprise</option>
          </select>
        </FilterToolbar>
        <table className="tbl tbl-flush">
          <thead><tr><th>Name</th><th>Title</th><th>Account</th><th>Role · active deal</th><th>ICP</th><th>Channel</th><th>Last contacted</th><th>Consent</th><th>Research</th></tr></thead>
          <tbody>
            {rows.map(c => {
              const a = getAccount(c.accountId);
              const { opp, role } = activeRoleFor(c);
              const roleTone = role === "Champion" ? "amber" : role === "Blocker" ? "red" : "slate";
              return (
                <tr key={c.id} onClick={() => navigate("sales/contacts/" + c.id)}>
                  <td><span className="tbl-link">{c.first} {c.last}</span></td>
                  <td className="muted">{c.title}</td>
                  <td>{a.name} <Badge tone="slate">{a.segment}</Badge></td>
                  <td>
                    {role
                      ? <><Badge tone={roleTone}>{role}</Badge>{opp ? <span className="muted" style={{ fontSize: 11, marginLeft: 6 }}>· {opp.name}</span> : null}</>
                      : <span className="muted">—</span>}
                  </td>
                  <td>{c.icp ? <MeddpiccIcon icp={c.icp} contactId={c.id} navigate={navigate} /> : <span className="muted">—</span>}</td>
                  <td className="muted">{c.channel}</td>
                  <td>{fmtDateShort(c.lastContact)}</td>
                  <td><Badge tone={c.gdpr === "Opted in" ? "green" : c.gdpr === "Unknown" ? "red" : "slate"}>{c.gdpr}</Badge></td>
                  <td><ResearchBadge surveys={c.surveys} interviews={c.interviews} /></td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
      {showNew && <NewContactDialog onClose={() => setShowNew(false)} navigate={navigate} />}
    </>
  );
}

function ContactDetail({ id, navigate }) {
  const c = getContact(id);
  const [tab, setTab] = useState("overview");
  const [action, setAction] = useState(null);
  if (!c) return <PageHead title="Not found" />;
  const a = getAccount(c.accountId) || {};
  const surveys = D.SURVEYS.filter(s => s.contactId === c.id);
  const interviews = D.INTERVIEWS.filter(i => i.contactId === c.id);
  // Deals this contact participates in — derived from the junction so we
  // pick up every role (champion, EB, primary, influencer, blocker, …),
  // not just the three legacy fixed slots.
  const dealRoles = getOppsForContact(c.id);          // [{ oppId, contactId, role, roleConfidence, isPrimary }]
  const deals = dealRoles.map(oc => getOpp(oc.oppId)).filter(Boolean);
  const activeOpp = getActiveOppForContact(c.id);
  const activeRole = activeOpp ? (getOppContactRole(activeOpp.id, c.id) || null) : null;
  const researchDates = [...surveys.map(s => s.completed || s.sent), ...interviews.map(i => i.date)].filter(Boolean).sort();
  const lastResearch = researchDates[researchDates.length - 1];
  const activities = (D.ACTIVITIES || []).filter(act => act.contactId === c.id);
  const contactEnrollments = (D.ENROLLMENTS || []).filter(e => e.contactId === c.id);

  const tabs = [
    { key: "overview", label: "Overview" },
    { key: "activity", label: "Activity" },
    { key: "sequences", label: "Sequences" },
    { key: "deals", label: "Deals", count: deals.length },
    { key: "research", label: "Research", count: surveys.length + interviews.length },
    { key: "audit", label: "Audit" },
  ];
  return (
    <>
      <PageHead
        title={`${c.first} ${c.last}`}
        sub={<>
          {c.title} · <a className="tbl-link" onClick={() => navigate("sales/accounts/" + a.id)}>{a.name}</a>
          {activeRole && activeRole.role
            ? <> · <Badge tone={activeRole.role === "Champion" ? "amber" : activeRole.role === "Blocker" ? "red" : "slate"}>{activeRole.role}</Badge>
                {activeOpp ? <span className="muted" style={{ fontSize: 12, marginLeft: 4 }}>on {activeOpp.name}</span> : null}</>
            : null}
          {c.icp && ICP_BY_SLUG[c.icp] ? <> · <Badge tone="slate">{ICP_BY_SLUG[c.icp].label}</Badge></> : null}
        </>}
        actions={<>
          <ResearchBadge surveys={c.surveys} interviews={c.interviews} />
          <button className="btn btn-sm" onClick={() => setAction("edit")}>Edit</button>
          {c.icp && ICP_BY_SLUG[c.icp]
            ? <MeddpiccIcon icp={c.icp} contactId={c.id} navigate={navigate} size="lg" />
            : <button className="btn btn-sm" onClick={() => setAction("icp")} title="Pick an ICP to enable the MEDDPICCR checklist">+ ICP · Open MEDDPICCR</button>}
          <a href={`mailto:${c.email}`} className="btn btn-sm">Email</a>
          <button className="btn btn-primary btn-sm" onClick={() => setAction("sequence")}>Add to sequence</button>
        </>}
      />
      <Tabs tabs={tabs} current={tab} onChange={setTab} />
      <div className="detail">
        <div>
          {tab === "overview" && (
            <div className="card"><div className="card-head"><div className="card-title">Identity & consent</div></div>
            <div className="card-body"><div className="grid grid-2" style={{ gap: 24 }}>
              <div className="kv">
                <div className="kv-label">Seniority</div><div className="kv-value">{c.seniority}</div>
                <div className="kv-label">Department</div><div className="kv-value">{c.dept}</div>
                <div className="kv-label">Email</div><div className="kv-value">{c.email}</div>
                <div className="kv-label">Phone</div><div className="kv-value">{c.phone}</div>
                <div className="kv-label">Channel pref</div><div className="kv-value">{c.channel}</div>
                <div className="kv-label">Source</div><div className="kv-value">{c.source || "—"}</div>
                <div className="kv-label">Touchpoints</div><div className="kv-value numeric">{c.touchpoints}</div>
              </div>
              <div className="kv">
                <div className="kv-label">Role · active deal</div>
                <div className="kv-value">
                  {activeRole && activeRole.role
                    ? <><Badge tone={activeRole.role === "Champion" ? "amber" : activeRole.role === "Blocker" ? "red" : "slate"}>{activeRole.role}</Badge>{activeOpp ? <span className="muted" style={{ fontSize: 12, marginLeft: 6 }}>on {activeOpp.name}</span> : null}</>
                    : <span className="muted">— (no open deal)</span>}
                </div>
                <div className="kv-label">Confidence</div>
                <div className="kv-value">
                  {activeRole && activeRole.roleConfidence
                    ? <>{"●".repeat(activeRole.roleConfidence)}<span className="muted">{"●".repeat(Math.max(0, 5 - activeRole.roleConfidence))}</span></>
                    : <span className="muted">—</span>}
                </div>
                <div className="kv-label">Email opt-out</div><div className="kv-value">{c.optOut ? <Badge tone="red">Yes</Badge> : "No"}</div>
                <div className="kv-label">GDPR consent</div><div className="kv-value"><Badge tone={c.gdpr === "Opted in" ? "green" : "slate"}>{c.gdpr}</Badge></div>
                <div className="kv-label">Last contacted</div><div className="kv-value">{fmtDate(c.lastContact)}</div>
                <div className="kv-label">Research fatigue</div><div className="kv-value">{c.fatigue ? <Badge tone="red">Yes — pause outreach</Badge> : <Badge tone="green">OK</Badge>}</div>
              </div>
            </div></div></div>
          )}
          {tab === "activity" && (
            <div className="card">
              <div className="card-head"><div className="card-title">Activity</div><div className="card-sub">calls · emails · meetings · notes with this contact</div></div>
              <ActivityTimeline activities={activities} empty="No activity recorded for this contact yet." />
            </div>
          )}
          {tab === "sequences" && (
            contactEnrollments.length === 0 ? (
              <div className="card"><div className="card-body muted">Not enrolled in any sequences. Use “Add to sequence” above to enroll this contact.</div></div>
            ) : (
              <div className="card"><table className="tbl tbl-flush">
                <thead><tr><th>Sequence</th><th>Status</th><th>Enrolled on</th></tr></thead>
                <tbody>{contactEnrollments.map(e => {
                  const seq = (D.SEQUENCES || []).find(x => x.id === e.sequenceId);
                  return (
                    <tr key={e.id} onClick={() => seq && navigate("sales/sequences/" + seq.id)}>
                      <td><span className="tbl-link">{seq ? seq.name : e.sequenceId}</span></td>
                      <td><Badge tone={e.status === "Meeting booked" ? "green" : e.status === "Replied" ? "blue" : e.status === "Exited" ? "red" : "slate"}>{e.status}</Badge></td>
                      <td>{fmtDateShort(e.enrolledOn)}</td>
                    </tr>
                  );
                })}</tbody>
              </table></div>
            )
          )}
          {tab === "deals" && (
            <div className="card"><table className="tbl tbl-flush"><thead><tr><th>Deal</th><th>Role</th><th>Confidence</th><th>Stage</th><th className="right">ACV</th></tr></thead><tbody>
              {dealRoles.map(oc => {
                const d = getOpp(oc.oppId);
                if (!d) return null;
                const tone = oc.role === "Champion" ? "amber" : oc.role === "Blocker" ? "red" : "slate";
                return (
                  <tr key={d.id} onClick={() => navigate("sales/opportunities/" + d.id)}>
                    <td><span className="tbl-link">{d.name}</span>{oc.isPrimary ? <span className="muted" style={{ fontSize: 11, marginLeft: 6 }}>· primary</span> : null}</td>
                    <td>{oc.role ? <Badge tone={tone}>{oc.role}</Badge> : <span className="muted">—</span>}</td>
                    <td>{oc.roleConfidence ? <>{"●".repeat(oc.roleConfidence)}<span className="muted">{"●".repeat(Math.max(0, 5 - oc.roleConfidence))}</span></> : <span className="muted">—</span>}</td>
                    <td><StageBadge stage={d.stage} /></td>
                    <td className="right numeric">{fmtMoney(d.acv, d.currency)}</td>
                  </tr>
                );
              })}
              {dealRoles.length === 0 && (
                <tr><td colSpan={5} className="muted" style={{ textAlign: "center", padding: 12 }}>This contact isn't attached to any opportunity yet.</td></tr>
              )}
            </tbody></table></div>
          )}
          {tab === "research" && (
            <div className="card">
              <div className="card-head"><div className="card-title">Research participation</div><div className="card-sub">{surveys.length + interviews.length} touchpoints{lastResearch ? ` · last ${fmtDate(lastResearch)}` : ""}</div></div>
              <div className="card-body">
                <div className="grid grid-4 mt-8" style={{ marginTop: 0, marginBottom: 12 }}>
                  <Stat label="Surveys sent" value={surveys.length} />
                  <Stat label="Surveys completed" value={surveys.filter(s => s.status === "Completed").length} />
                  <Stat label="Interviews completed" value={interviews.filter(i => i.status === "Completed").length} />
                  <Stat label="Fatigue flag" value={c.fatigue ? "ON" : "OK"} />
                </div>
                <table className="tbl tbl-flush">
                  <thead><tr><th>Type</th><th>Form</th><th>Status</th><th>Date</th><th>Strength / Reach</th></tr></thead>
                  <tbody>
                    {surveys.map(s => <tr key={s.id} onClick={() => navigate("research/surveys/" + s.id)}><td>{s.type}</td><td><Badge tone="blue">Survey</Badge></td><td>{s.status}</td><td>{fmtDateShort(s.completed || s.sent)}</td><td>{s.championStrength || s.reach || "—"}</td></tr>)}
                    {interviews.map(i => <tr key={i.id} onClick={() => navigate("research/interviews/" + i.id)}><td>{i.type}</td><td><Badge tone="amber">Interview</Badge></td><td>{i.status}</td><td>{fmtDateShort(i.date)}</td><td>{i.championStrength || i.reach || "—"}</td></tr>)}
                  </tbody>
                </table>
              </div>
            </div>
          )}
          {tab === "audit" && (
            <div className="card"><div className="card-body"><div className="kv">
              <div className="kv-label">Contact ID</div><div className="kv-value-mono">{c.id}</div>
              <div className="kv-label">Last contacted</div><div className="kv-value">{fmtDate(c.lastContact)}</div>
            </div></div></div>
          )}
        </div>
        <RightRail record={c} link={{ contactId: c.id, accountId: c.accountId }} />
      </div>
      {action === "edit" && <EditContactDialog contact={c} onClose={() => setAction(null)} />}
      {action === "sequence" && <AddToSequenceDialog contact={c} onClose={() => setAction(null)} />}
      {action === "icp" && <PickIcpDialog contact={c} onClose={() => setAction(null)} navigate={navigate} />}
    </>
  );
}

function PickIcpDialog({ contact, onClose, navigate }) {
  const [icp, setIcp] = useState(contact.icp || ICP_OPTIONS[0].slug);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  async function handleSave() {
    setSaving(true); setError(null);
    try {
      await tpAPI.contacts.update(contact.id, { icp });
      onClose();
      navigate("sales/contacts/" + contact.id + "/meddpicc");
    } catch (e) {
      setError(e.message || "Failed to update contact.");
      setSaving(false);
    }
  }
  return (
    <Dialog title="Assign ICP" onClose={onClose} footer={<>
      <button className="btn btn-sm" onClick={onClose}>Cancel</button>
      <button className="btn btn-primary btn-sm" onClick={handleSave} disabled={saving}>{saving ? "Saving…" : "Save & open MEDDPICCR"}</button>
    </>}>
      <p className="muted" style={{ margin: 0, fontSize: 13 }}>Pick the persona that best matches <strong>{contact.first} {contact.last}</strong>. This drives which MEDDPICCR checklist opens.</p>
      <div className="form-field">
        <label>ICP persona</label>
        <select value={icp} onChange={e => setIcp(e.target.value)}>
          {ICP_OPTIONS.map(o => <option key={o.slug} value={o.slug}>{o.label}</option>)}
        </select>
      </div>
      {error && <div className="form-error">{error}</div>}
    </Dialog>
  );
}

// MEDDPICCR page — iframes the standalone /intcrm/meddpicc/<slug>.html page
// and persists check state to /apicrm/meddpicc via postMessage. Responses
// are deal-scoped: the same contact will have different answers about the
// starter deal vs an expansion (different metrics, paper process, etc.),
// so the row key is (oppId, contactId, icp). The page picks the contact's
// most-recent open opportunity by default and exposes a switcher to flip
// context — the iframe is rekeyed on switch so it reloads fresh state.
function MeddpiccPage({ id, navigate }) {
  const c = getContact(id);
  const iframeRef = useRef(null);
  const stateRef  = useRef({});
  const saveTimer = useRef(null);
  const [status, setStatus] = useState("loading"); // loading | ready | saving | saved | error
  const [error, setError]   = useState(null);

  // Compute available opportunities first — needed before the early returns
  // below so hook order stays stable across re-renders.
  const oppRows = c ? getOppsForContact(c.id) : [];
  const opps = oppRows.map(oc => getOpp(oc.oppId)).filter(Boolean);
  const defaultOpp = c ? getActiveOppForContact(c.id) : null;
  const defaultOppId = defaultOpp ? defaultOpp.id : (opps[0] ? opps[0].id : null);
  const [oppId, setOppId] = useState(defaultOppId);

  if (!c) return <PageHead title="Not found" />;
  if (!c.icp || !ICP_BY_SLUG[c.icp]) {
    return (
      <>
        <PageHead title={`${c.first} ${c.last} · MEDDPICCR`} sub="No ICP assigned" actions={<button className="btn btn-sm" onClick={() => navigate("sales/contacts/" + c.id)}>← Back</button>} />
        <div className="card"><div className="card-body">
          <p>This contact has no ICP persona assigned. Open the contact and click <strong>+ ICP</strong> to pick one before running the MEDDPICCR checklist.</p>
        </div></div>
      </>
    );
  }
  if (!oppId) {
    return (
      <>
        <PageHead title={`${c.first} ${c.last} · MEDDPICCR`} sub="No deal context" actions={<button className="btn btn-sm" onClick={() => navigate("sales/contacts/" + c.id)}>← Back</button>} />
        <div className="card"><div className="card-body">
          <p>MEDDPICCR qualifies a <strong>deal</strong>, so this contact needs to be attached to at least one opportunity before the checklist can be filled out. Open an opportunity and add <strong>{c.first} {c.last}</strong> in the Stakeholders panel.</p>
        </div></div>
      </>
    );
  }
  const icp  = c.icp;
  const meta = ICP_BY_SLUG[icp];
  const opp  = getOpp(oppId);
  const src  = `/intcrm/meddpicc/${icp}.html?oppId=${encodeURIComponent(oppId)}&contactId=${encodeURIComponent(c.id)}&icp=${encodeURIComponent(icp)}`;

  // Re-fetch responses + rebind the iframe each time the deal context changes.
  useEffect(() => {
    let cancelled = false;
    async function postState() {
      try {
        const row = await tpAPI.meddpicc.get(oppId, c.id, icp);
        if (cancelled) return;
        stateRef.current = (row && row.responses) || {};
        const iframe = iframeRef.current;
        if (iframe && iframe.contentWindow) {
          iframe.contentWindow.postMessage(
            { type: "meddpicc:state", oppId, contactId: c.id, icp, responses: stateRef.current },
            "*"
          );
        }
        setStatus("ready");
      } catch (e) {
        if (cancelled) return;
        setError(e.message || "Failed to load responses");
        setStatus("error");
      }
    }

    function flushSave(responses) {
      if (saveTimer.current) clearTimeout(saveTimer.current);
      saveTimer.current = setTimeout(async () => {
        saveTimer.current = null;
        setStatus("saving");
        try {
          await tpAPI.meddpicc.save(oppId, c.id, icp, responses);
          setStatus("saved");
        } catch (e) {
          setError(e.message || "Failed to save responses");
          setStatus("error");
        }
      }, 350);
    }

    function onMessage(ev) {
      // Ignore messages from anything other than our iframe.
      if (!iframeRef.current || ev.source !== iframeRef.current.contentWindow) return;
      const d = ev.data || {};
      // The iframe also broadcasts oppId now — drop messages from a stale
      // iframe instance (after a deal switch) so we don't overwrite the
      // new deal's responses with the previous one's.
      if (d.oppId && d.oppId !== oppId) return;
      if (d.type === "meddpicc:request-state") postState();
      else if (d.type === "meddpicc:save")    flushSave(d.responses || {});
    }

    setStatus("loading");
    window.addEventListener("message", onMessage);
    return () => {
      cancelled = true;
      window.removeEventListener("message", onMessage);
      if (saveTimer.current) { clearTimeout(saveTimer.current); saveTimer.current = null; }
    };
  }, [c.id, icp, oppId]);

  const statusBadge = status === "saving" ? <Badge tone="slate">Saving…</Badge>
                    : status === "saved"  ? <Badge tone="green">Saved</Badge>
                    : status === "error"  ? <Badge tone="red">{error || "Error"}</Badge>
                    : status === "ready"  ? <Badge tone="slate">Loaded</Badge>
                    : <Badge tone="slate">Loading…</Badge>;

  return (
    <>
      <PageHead
        title={`${c.first} ${c.last} · MEDDPICCR`}
        sub={<>{meta.label} · responses persist per deal</>}
        actions={<>
          {opps.length > 1 && (
            <select
              value={oppId}
              onChange={e => setOppId(e.target.value)}
              title="Switch deal context"
              style={{ fontSize: 13, padding: "4px 8px" }}
            >
              {opps.map(o => <option key={o.id} value={o.id}>{o.name}</option>)}
            </select>
          )}
          {statusBadge}
          <button className="btn btn-sm" onClick={() => navigate("sales/contacts/" + c.id)}>← Back to contact</button>
        </>}
      />
      <div className="card" style={{ padding: "8px 12px", marginBottom: 8 }}>
        <span className="muted" style={{ fontSize: 12.5 }}>Deal context:</span>{" "}
        {opp ? <><strong>{opp.name}</strong> · <StageBadge stage={opp.stage} /></> : <em className="muted">unknown</em>}
      </div>
      <div className="card" style={{ padding: 0, overflow: "hidden" }}>
        <iframe
          key={oppId}
          ref={iframeRef}
          src={src}
          title={`MEDDPICCR · ${meta.label}`}
          style={{ width: "100%", minHeight: "calc(100vh - 260px)", border: 0, display: "block" }}
        />
      </div>
    </>
  );
}

window.ContactsList = ContactsList;
window.ContactDetail = ContactDetail;
window.MeddpiccPage = MeddpiccPage;
